import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/auth';
import { Button } from '../ui/Button';
import { useState, useRef } from 'react';
import { resolveAssetUrl } from '@/utils/assetUrl';
import { useConfig } from '@/context/ConfigContext';
import { getPlaceholderAvatar } from '@/utils/avatars';
import { TaleemXMark, TaleemXWordmark, isDefaultBranding } from '@/components/brand';

type TopBarProps = {
  title: string;
  onToggleSidebar?: () => void;
};

export function TopBar({ title, onToggleSidebar }: TopBarProps) {
  const { user, logout, updateUser } = useAuth();
  const { config } = useConfig();
  const [open, setOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const isTeacher = user?.role?.toLowerCase() === 'teacher';

  const handleToggle = () => setOpen(v => !v);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isTeacher) return;
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('[TopBar] file selected', file.name, file.size, file.type);

    const MAX_DIM = 256; // max width/height in px

    // create image object from file
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      try {
        // compute scale: do not upscale, only downscale when necessary
        const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        const targetW = Math.round(img.width * scale);
        const targetH = Math.round(img.height * scale);

        // draw to canvas at target size
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context unavailable');
        ctx.drawImage(img, 0, 0, targetW, targetH);

        // preview: use data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        if (updateUser && user) {
          updateUser({ ...user, photo: dataUrl });
        }

        // prepare blob for upload
        const blob: Blob | null = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
        if (!blob) throw new Error('Failed to create image blob');

        // create a File from blob preserving a reasonable filename
        const uploadFile = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });

        // call API to upload avatar and get updated user
        const updated = await authService.uploadAvatar(uploadFile);
        console.log('[TopBar] uploadAvatar completed, new photo:', updated.photo ?? '(none)');
        if (updateUser) updateUser(updated as any);
      } catch (err) {
        console.error('Avatar processing/upload failed', err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = err => {
      console.error('Failed to load image for processing', err);
      URL.revokeObjectURL(url);
    };
    img.src = url;

    // close dropdown after selection
    setOpen(false);
  };

  return (
    <header className="flex h-16 sm:h-20 items-center justify-between border-b border-slate-200 bg-white px-3 sm:px-6 shrink-0">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        {onToggleSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            className="rounded-lg p-2 text-slate-500 transition hover:text-slate-700 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand min-h-[40px] min-w-[40px] flex items-center justify-center shrink-0"
            aria-label="Toggle navigation menu"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
            </svg>
          </button>
        )}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="inline-flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-slate-50 overflow-hidden shrink-0">
            {isDefaultBranding(config) ? (
              <TaleemXMark size="sm" className="h-6 w-6 sm:h-7 sm:w-7" />
            ) : (
              <img
                src={resolveAssetUrl(config?.school_logo_url) || '/logo-mark.svg'}
                alt={config?.school_name || 'School Logo'}
                className="h-6 w-6 sm:h-8 sm:w-8 object-contain"
              />
            )}
          </div>
          <div className="min-w-0">
            {isDefaultBranding(config) ? (
              <TaleemXWordmark size="md" tone="dark" className="truncate block" />
            ) : (
              <div className="text-base sm:text-lg font-semibold text-slate-900 truncate">
                {config?.school_name}
              </div>
            )}
            <p className="hidden text-sm text-slate-500 sm:block truncate">{title}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:gap-4 shrink-0">
        <div className="relative flex items-center gap-2 sm:gap-3">
          <button type="button" onClick={handleToggle} aria-label="User menu" className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 overflow-hidden ring-2 ring-slate-200 hover:ring-brand/40 transition-all shrink-0">
            <img src={resolveAssetUrl(user?.photo) || getPlaceholderAvatar(user?.role || 'admin')} alt="avatar" className="h-full w-full object-cover" />
          </button>
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium text-slate-900 truncate max-w-[120px] md:max-w-none">
              {user ? `${user.firstName} ${user.lastName}` : 'Guest'}
            </p>
            <p className="hidden text-xs text-slate-500 md:block">{user?.email ?? ''}</p>
          </div>

          {open && (
            <div className="absolute right-0 top-full mt-2 w-48 rounded-lg border bg-white p-1.5 shadow-xl z-50">
              {!isTeacher && (
                <>
                  <button className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100 min-h-[38px] flex items-center" onClick={() => fileRef.current?.click()}>
                    Change photo
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                </>
              )}
              <button className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100 text-rose-600 min-h-[38px] flex items-center" onClick={() => { logout(); setOpen(false); }}>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
